/* ═══════════════════════════════════════════════════════════════════════════
   KHARI FOODS — khari.js  v2026-05-02-B
   Global: Cart AJAX · Mobile nav · Scarcity badges · Wishlist · Sticky ATC
   ═══════════════════════════════════════════════════════════════════════════ */

'use strict';

const Khari = (() => {

  /* ─── Scarcity badge data (per product handle) ─────────────────────────── */
  const SCARCITY_BASE = {
    'daily-trail-mix-8-in-1-snack-mix-200g': 34,
    'roasted-seeds-berries-crunch-250g': 22,
    'makhana-pop-25g-mint-pudina': 18,
    'makhana-pop-25g-peri-peri': 21,
    'mini-snack-bar-dates-nuts': 29,
    'snack-bars-honey-fruits': 27,
    'jowar-pops-tomato-cheese-50g': 19,
    'beetroot-crispies-50g-peri-peri-chips': 15,
    'oats-crispies-50g-masala-flavour-no-palm-oil': 13,
    'very-berry-mix-250g': 16,
    'original-premium-kimia-dates': 24,
    'jumbo-premium-medjoul-dates': 11,
    'premium-ajwa-dates': 14,
    'health-gift-box-100g-x-4': 8,
    'dry-fruits-gift-box-100g-x-4': 9,
  };

  function getScarcityCount(handle) {
    const key = `ks_${handle}`;
    let stored = sessionStorage.getItem(key);
    if (stored) return parseInt(stored);
    // Base ± random ±4 so it looks organic
    const base = SCARCITY_BASE[handle] || (Math.floor(Math.random() * 14) + 7);
    const count = base + Math.floor(Math.random() * 9) - 4;
    const final = Math.max(4, count);
    sessionStorage.setItem(key, final);
    return final;
  }

  /* ─── Cart AJAX ─────────────────────────────────────────────────────────── */
  async function addToCart(variantId, quantity = 1, btn) {
    if (!variantId) return;
    if (btn) {
      btn.disabled = true;
      const orig = btn.innerHTML;
      btn.innerHTML = '<span>Adding…</span>';
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity }),
        });
        if (!res.ok) throw new Error('Cart error');
        btn.innerHTML = '<span>✓ Added!</span>';
        btn.classList.add('added');
        await updateCartCount();
        // Open cart drawer after short delay so user sees the button feedback first
        setTimeout(() => openCartDrawer(), 500);
        setTimeout(() => {
          btn.innerHTML = orig;
          btn.classList.remove('added');
          btn.disabled = false;
        }, 2000);
      } catch (e) {
        btn.innerHTML = orig;
        btn.disabled = false;
        console.error('[Khari] Cart add failed:', e);
      }
    }
  }

  /* ─── Quantity selector ─────────────────────────────────────────────────── */
  function initQtySelectors() {
    document.querySelectorAll('.qty-selector').forEach(sel => {
      const minusBtn = sel.querySelector('[data-action="minus"]');
      const plusBtn  = sel.querySelector('[data-action="plus"]');
      const numEl    = sel.querySelector('.qty-num');
      if (!minusBtn || !numEl) return;
      let qty = parseInt(numEl.textContent) || 1;

      minusBtn.addEventListener('click', () => {
        if (qty > 1) { qty--; numEl.textContent = qty; }
      });
      plusBtn?.addEventListener('click', () => {
        if (qty < 99) { qty++; numEl.textContent = qty; }
      });
    });

    // Inline card qty (collection page)
    document.querySelectorAll('.prod-card').forEach(card => {
      const addBtn  = card.querySelector('.add-btn');
      if (!addBtn) return;

      const qtyWrap = card.querySelector('.inline-qty');

      if (qtyWrap) {
        // Progressive disclosure: first click shows qty stepper, second click adds to cart
        const minusBtn = qtyWrap.querySelector('[data-action="minus"]');
        const plusBtn  = qtyWrap.querySelector('[data-action="plus"]');
        const valEl    = qtyWrap.querySelector('.qty-val');
        let qty = 1;

        addBtn.addEventListener('click', async () => {
          const variantId = addBtn.dataset.variantId;
          if (!variantId) { window.location = addBtn.dataset.href || '#'; return; }

          if (!qtyWrap.classList.contains('shown')) {
            qtyWrap.style.display = 'flex';
            qtyWrap.classList.add('shown', 'active');
            addBtn.innerHTML = '🛒';
          } else {
            await addToCart(variantId, qty, addBtn);
            qty = 1; valEl.textContent = 1;
          }
        });

        minusBtn?.addEventListener('click', () => { if (qty > 1) { qty--; valEl.textContent = qty; } });
        plusBtn?.addEventListener('click',  () => { if (qty < 99) { qty++; valEl.textContent = qty; } });

      } else {
        // Simple direct ATC — no inline qty stepper on this card
        addBtn.addEventListener('click', async () => {
          const variantId = addBtn.dataset.variantId;
          if (!variantId) { window.location = addBtn.dataset.href || '#'; return; }
          await addToCart(variantId, 1, addBtn);
        });
      }
    });

    // Card variant dropdown — sync selected variant to ATC button + update price
    document.querySelectorAll('.card-variant-select').forEach(sel => {
      const card    = sel.closest('.prod-card');
      const addBtn  = card?.querySelector('.add-btn');
      const priceEl = card?.querySelector('.prod-price');
      const mrpEl   = card?.querySelector('.prod-mrp');
      const saveEl  = card?.querySelector('.prod-save');

      sel.addEventListener('change', () => {
        const opt = sel.options[sel.selectedIndex];
        if (!opt) return;

        // Sync variant ID to ATC button
        if (addBtn) addBtn.dataset.variantId = opt.value;

        // Update price display
        const newPrice   = opt.dataset.price;
        const newCompare = opt.dataset.compare;
        const newSave    = opt.dataset.save;
        if (priceEl && newPrice) priceEl.textContent = newPrice;
        if (mrpEl) mrpEl.textContent = (newCompare && newCompare !== newPrice) ? newCompare : '';
        if (saveEl) {
          if (newSave) {
            saveEl.textContent = newSave;
            saveEl.style.display = 'inline-block';
          } else {
            saveEl.style.display = 'none';
          }
        }
      });
    });
  }

  /* ─── Scarcity badges ───────────────────────────────────────────────────── */
  function initScarcityBadges() {
    document.querySelectorAll('.qty-sold-badge[data-handle]').forEach(badge => {
      const handle = badge.dataset.handle;
      const count = getScarcityCount(handle);
      const countEl = badge.querySelector('.sold-count');
      if (countEl) countEl.textContent = count;
      // Show after 1.2s for realism
      setTimeout(() => badge.classList.add('visible'), 1200 + Math.random() * 600);
    });
  }

  /* ─── Wishlist (localStorage) ───────────────────────────────────────────── */
  function initWishlist() {
    const KEY = 'khari_wishlist';
    const getList = () => JSON.parse(localStorage.getItem(KEY) || '[]');
    const setList = (l) => localStorage.setItem(KEY, JSON.stringify(l));

    document.querySelectorAll('.wishlist-btn').forEach(btn => {
      const handle = btn.dataset.handle;
      const list = getList();
      if (list.includes(handle)) { btn.textContent = '❤️'; btn.classList.add('liked'); }

      btn.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        const l = getList();
        const idx = l.indexOf(handle);
        if (idx === -1) {
          l.push(handle); btn.textContent = '❤️'; btn.classList.add('liked');
        } else {
          l.splice(idx, 1); btn.textContent = '🤍'; btn.classList.remove('liked');
        }
        setList(l);
        btn.style.transform = 'scale(1.4)';
        setTimeout(() => { btn.style.transform = ''; }, 300);
      });
    });
  }

  /* ─── Mobile hamburger nav ──────────────────────────────────────────────── */
  /* ─── Scroll lock (reference-counted — multiple callers safe) ───────── */
  let _scrollLocks = 0;
  let _savedScrollY = 0;
  function lockScroll() {
    if (++_scrollLocks === 1) {
      _savedScrollY = window.scrollY;
      document.body.style.overflow  = 'hidden';
      document.body.style.position  = 'fixed';
      document.body.style.top       = `-${_savedScrollY}px`;
      document.body.style.width     = '100%';
    }
  }
  function unlockScroll() {
    if (--_scrollLocks <= 0) {
      _scrollLocks = 0;
      document.body.style.overflow  = '';
      document.body.style.position  = '';
      document.body.style.top       = '';
      document.body.style.width     = '';
      window.scrollTo(0, _savedScrollY);
    }
  }

  function initMobileNav() {
    const ham = document.querySelector('.hamburger');
    const nav = document.querySelector('.mobile-nav');
    if (!ham || !nav) return;
    ham.addEventListener('click', () => {
      const open = ham.classList.toggle('open');
      if (open) { nav.classList.add('open');    lockScroll(); }
      else       { nav.classList.remove('open'); unlockScroll(); }
    });

    // Close on nav link click
    nav.querySelectorAll('a').forEach(a => {
      a.addEventListener('click', () => {
        if (ham.classList.contains('open')) {
          ham.classList.remove('open'); nav.classList.remove('open');
          unlockScroll();
        }
      });
    });
  }

  /* ─── Product page: gallery thumbnails + auto-scroll ───────────────────── */
  function initGallery() {
    const mainImg  = document.querySelector('.gallery-main img');
    const mainWrap = document.querySelector('.gallery-main');
    if (!mainImg) return;

    const thumbs = Array.from(document.querySelectorAll('.thumb'));
    if (!thumbs.length) return;

    let currentIdx = 0;
    let autoTimer  = null;
    let paused     = false;

    function activateThumb(idx) {
      thumbs.forEach(t => t.classList.remove('active'));
      thumbs[idx].classList.add('active');
      const src = thumbs[idx].dataset.src || thumbs[idx].querySelector('img')?.src;
      if (src) {
        mainImg.style.opacity = '0';
        setTimeout(() => {
          mainImg.src = src;
          mainImg.style.opacity = '1';
        }, 160);
      }
      // Scroll only within the thumb rail — never move the page
      const rail = thumbs[idx].closest('.gallery-thumbs');
      if (rail) {
        const th = thumbs[idx];
        const offset = th.offsetLeft - rail.offsetWidth / 2 + th.offsetWidth / 2;
        rail.scrollTo({ left: offset, behavior: 'smooth' });
      }
    }

    function startAuto() {
      clearInterval(autoTimer);
      autoTimer = setInterval(() => {
        if (!paused && thumbs.length > 1) {
          currentIdx = (currentIdx + 1) % thumbs.length;
          activateThumb(currentIdx);
        }
      }, 3000);
    }

    // Click handler
    thumbs.forEach((th, i) => {
      th.addEventListener('click', () => {
        currentIdx = i;
        activateThumb(i);
        // Reset timer so it doesn't jump right after manual pick
        startAuto();
      });
    });

    // Pause on gallery hover
    mainWrap?.addEventListener('mouseenter', () => { paused = true; });
    mainWrap?.addEventListener('mouseleave', () => { paused = false; });

    // Fade transition style
    mainImg.style.transition = 'opacity .16s ease';

    startAuto();
  }

  /* ─── Product page: tabs ────────────────────────────────────────────────── */
  function initTabs() {
    document.querySelectorAll('.tab-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const tabId = btn.dataset.tab;
        document.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
        document.querySelectorAll('.tab-content').forEach(c => c.classList.remove('active'));
        btn.classList.add('active');
        document.getElementById(`tab-${tabId}`)?.classList.add('active');
      });
    });
  }

  /* ─── Product page: variant selection ──────────────────────────────────── */
  function initVariants() {
    const pills = document.querySelectorAll('.vpill');
    if (!pills.length) return;

    // Target by ID — the actual IDs in main-product.liquid
    const priceEl      = document.getElementById('price-display');
    const mrpEl        = document.getElementById('mrp-display');
    const saveEl       = document.getElementById('save-display');
    const atcMain      = document.querySelector('.atc-main');
    const stickyBtn    = document.querySelector('.sticky-atc-btn');
    const stickyPri    = document.querySelector('.sticky-prod-price');
    const variantLabel = document.getElementById('selected-variant-label');

    pills.forEach(pill => {
      pill.addEventListener('click', () => {
        if (pill.classList.contains('disabled')) return;

        // Remove any existing "Selected" sub-label from ALL pills
        // (covers both server-rendered and dynamically added spans)
        pills.forEach(p => {
          p.classList.remove('active');
          p.querySelectorAll('.vpill-sub').forEach(s => {
            if (s.textContent.trim() === 'Selected') s.remove();
          });
        });

        // Mark clicked pill active + add "Selected" label
        pill.classList.add('active');
        const selSpan = document.createElement('span');
        selSpan.className = 'vpill-sub';
        selSpan.textContent = 'Selected';
        pill.appendChild(selSpan);

        // Update variant label above pills
        const title = pill.dataset.title || '';
        if (variantLabel && title) variantLabel.textContent = title;

        // Parse prices (variant.price | times: 0.01 → rupee float)
        const price  = parseFloat(pill.dataset.price || 0);
        const mrpRaw = parseFloat(pill.dataset.mrp  || 0);
        const mrp    = mrpRaw > 0 ? mrpRaw : price;
        const vid    = pill.dataset.variantId;

        // Update displayed sale price
        if (priceEl) priceEl.textContent = `₹${price.toFixed(0)}`;

        // Update MRP strikethrough + save badge
        if (mrp > price) {
          if (mrpEl)  { mrpEl.textContent = `₹${mrp.toFixed(0)}`; mrpEl.style.display = ''; }
          if (saveEl) {
            const pct = Math.round((mrp - price) / mrp * 100);
            saveEl.textContent = `Save ${pct}%`;
            saveEl.style.display = 'inline-flex';
          }
        } else {
          if (mrpEl)  { mrpEl.textContent = ''; mrpEl.style.display = 'none'; }
          if (saveEl) saveEl.style.display = 'none';
        }

        // Sync variant ID + prices to sticky bar
        if (atcMain)   atcMain.dataset.variantId  = vid;
        if (stickyBtn) stickyBtn.dataset.variantId = vid;
        if (stickyPri) stickyPri.textContent = `₹${price.toFixed(0)}`;
        const stickyMrp = document.getElementById('sticky-mrp');
        if (stickyMrp) {
          if (mrp > price) {
            stickyMrp.textContent = `₹${mrp.toFixed(0)}`;
            stickyMrp.style.display = '';
          } else {
            stickyMrp.textContent = '';
            stickyMrp.style.display = 'none';
          }
        }
      });
    });
  }

  /* ─── Sticky ATC bar (product page) ────────────────────────────────────── */
  function initStickyAtc() {
    const bar     = document.querySelector('.sticky-atc');
    const mainAtc = document.querySelector('.atc-main');
    if (!bar) return;

    const stickyBtn  = bar.querySelector('.sticky-atc-btn');
    const qtyMinus   = document.getElementById('sticky-qty-minus');
    const qtyPlus    = document.getElementById('sticky-qty-plus');
    const qtyDisplay = document.getElementById('sticky-qty-num');
    let stickyQty    = 1;

    // Qty controls
    qtyMinus?.addEventListener('click', () => {
      if (stickyQty > 1) stickyQty--;
      if (qtyDisplay) qtyDisplay.textContent = stickyQty;
    });
    qtyPlus?.addEventListener('click', () => {
      stickyQty++;
      if (qtyDisplay) qtyDisplay.textContent = stickyQty;
    });

    // On product page: hide bar when product title is visible, show when scrolled past
    const titleEl = document.querySelector('.prod-info-title');
    if (titleEl && window.innerWidth <= 960) {
      // Start hidden, fade in after title leaves view
      bar.style.opacity = '0';
      bar.style.pointerEvents = 'none';
      const obs = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting;
        bar.style.opacity      = show ? '1' : '0';
        bar.style.pointerEvents = show ? 'all' : 'none';
      }, { threshold: 0.1 });
      obs.observe(titleEl);
    }

    stickyBtn?.addEventListener('click', async () => {
      const vid = stickyBtn.dataset.variantId;
      await addToCart(vid, stickyQty, stickyBtn);
    });

    mainAtc?.addEventListener('click', async () => {
      const vid = mainAtc.dataset.variantId;
      await addToCart(vid, stickyQty, mainAtc);
    });
  }

  /* ─── Reviews paginator ─────────────────────────────────────────────────── */
  function initReviewsPaginator() {
    const list    = document.getElementById('rv-list');
    const dotsEl  = document.getElementById('rv-dots');
    const fillEl  = document.getElementById('rv-progress-fill');
    if (!list || !dotsEl) return;

    const PER_PAGE = 5;
    const INTERVAL = 10000; // 10s
    const items = Array.from(list.querySelectorAll('.rv-item'));
    if (items.length <= PER_PAGE) return; // no pagination needed

    const totalPages = Math.ceil(items.length / PER_PAGE);
    let currentPage  = 0;
    let timer, fillTimer;

    // Build dots
    dotsEl.innerHTML = '';
    for (let i = 0; i < totalPages; i++) {
      const d = document.createElement('button');
      d.className = 'rv-dot' + (i === 0 ? ' active' : '');
      d.setAttribute('aria-label', 'Page ' + (i + 1));
      d.addEventListener('click', () => { goTo(i); resetTimer(); });
      dotsEl.appendChild(d);
    }

    function showPage(page) {
      items.forEach((el, idx) => {
        const onPage = Math.floor(idx / PER_PAGE) === page;
        el.classList.toggle('rv-hidden', !onPage);
      });
      dotsEl.querySelectorAll('.rv-dot').forEach((d, i) => {
        d.classList.toggle('active', i === page);
      });
      currentPage = page;
      animateProgress();
    }

    function goTo(page) {
      showPage(page % totalPages);
    }

    function animateProgress() {
      if (fillEl) {
        fillEl.style.transition = 'none';
        fillEl.style.width = '0%';
        requestAnimationFrame(() => {
          fillEl.style.transition = 'width ' + (INTERVAL / 1000) + 's linear';
          fillEl.style.width = '100%';
        });
      }
    }

    function resetTimer() {
      clearInterval(timer);
      timer = setInterval(() => goTo((currentPage + 1) % totalPages), INTERVAL);
      animateProgress();
    }

    showPage(0);
    resetTimer();
  }


  // ── Khari Discount Offers ────────────────────────────────────────────────
  // Discount data is injected by Liquid (theme.liquid → window.__khariDiscounts)
  // from Theme Settings → Cart Coupons → Discount Offers (JSON).
  // No network call needed — instant, zero infrastructure.
  let _discountsCache = null;

  function fetchDiscounts() {
    if (_discountsCache !== null) return Promise.resolve(_discountsCache);
    try {
      const raw = window.__khariDiscounts;
      if (Array.isArray(raw) && raw.length) {
        // Sort ascending by min_amount so cheapest unlock shows first
        _discountsCache = [...raw].sort((a, b) => (a.min_amount || 0) - (b.min_amount || 0));
        return Promise.resolve(_discountsCache);
      }
    } catch { /* ignore */ }
    _discountsCache = [];
    return Promise.resolve([]);
  }

  // Render offer rows inside the cart drawer
  function renderCartOffers(discounts) {
    const container = document.getElementById('khari-cart-offers');
    if (!container) return;
    if (!discounts.length) { container.innerHTML = ''; return; }
    container.innerHTML = discounts.map(d => `
      <div class="cof-offer-row">
        <div class="cof-row">
          <div class="cof-row-info">
            <div class="cof-row-title">Shop ₹${d.min_amount}+ &amp; get <strong>${d.label}</strong></div>
            <div class="cof-row-sub">Code: <span class="cof-code-text">${d.code}</span></div>
          </div>
          <button class="cof-tap-apply-btn"
            data-code="${d.code}"
            data-min="${d.min_amount}"
            data-discount-pct="${d.discount_pct || 0}"
            aria-label="Apply ${d.code}">Tap to apply</button>
        </div>
      </div>
    `).join('');
  }

  // Render offer cards on the product detail page
  function renderPdpOffers(discounts) {
    const container = document.getElementById('pdp-offers-dynamic');
    if (!container) return;
    if (!discounts.length) {
      container.innerHTML = '<div class="pdp-offer-empty"><span>🏷️</span> No offers available right now</div>';
      return;
    }
    const copyIcon = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>`;
    container.innerHTML = discounts.map(d => `
      <div class="pdp-offer-card">
        <span class="pdp-offer-icon">🏷️</span>
        <div class="pdp-offer-body">
          <div class="pdp-offer-title">Shop ₹${d.min_amount}+ &amp; get <strong>${d.label}</strong></div>
          <div class="pdp-offer-sub">Use code at checkout</div>
        </div>
        <button class="pdp-offer-code" data-code="${d.code}" aria-label="Copy coupon code ${d.code}">
          ${d.code} ${copyIcon}
        </button>
      </div>
    `).join('');
  }

  // Fetch + render both surfaces
  async function loadDiscounts() {
    const discounts = await fetchDiscounts();
    renderCartOffers(discounts);
    renderPdpOffers(discounts);
    refreshOfferButtons();
  }

  // ── Module-level offer button state (callable from initCartCoupon too) ──
  function updateOfferButtons(cartTotalRupees) {
    document.querySelectorAll('.cof-tap-apply-btn').forEach(btn => {
      const min      = parseInt(btn.dataset.min || '0', 10);
      const eligible = cartTotalRupees >= min;
      btn.disabled   = !eligible;
      btn.title      = eligible ? '' : `Add ₹${min - cartTotalRupees} more to unlock`;
      const sub = btn.closest('.cof-row')?.querySelector('.cof-row-sub');
      if (sub) {
        if (eligible) {
          sub.innerHTML = `Code: <span class="cof-code-text">${btn.dataset.code}</span>`;
        } else {
          sub.textContent = `Add ₹${min - cartTotalRupees} more to unlock`;
        }
      }
    });
  }

  function refreshOfferButtons() {
    fetch('/cart.js')
      .then(r => r.json())
      .then(c => updateOfferButtons(Math.floor(c.total_price / 100)))
      .catch(() => {});
  }

  /* ─── Coupon code copy (PDP + Cart Drawer) ──────────────────────────────── */
  function initOfferCopy() {
    // Event delegation — works for both static and dynamically rendered cards
    document.addEventListener('click', e => {
      const btn = e.target.closest('.pdp-offer-code');
      if (!btn) return;
      const code = btn.dataset.code;
      if (!code) return;
      navigator.clipboard?.writeText(code).catch(() => {});
      const orig = btn.innerHTML;
      btn.classList.add('copied');
      btn.innerHTML = '✔ Copied!';
      setTimeout(() => { btn.classList.remove('copied'); btn.innerHTML = orig; }, 2000);
    });

    // Cart drawer "Tap to apply" — event delegation
    document.addEventListener('click', e => {
      const btn = e.target.closest('.cof-tap-apply-btn');
      if (!btn) return;
      const code = btn.dataset.code;
      if (!code || btn.disabled) return;
      const input    = document.getElementById('cart-coupon-input');
      const applyBtn = document.getElementById('cart-coupon-apply-btn');
      if (input)    input.value = code;
      if (applyBtn) applyBtn.click();
    });

    // Refresh button states on cart events
    document.addEventListener('cart:updated', refreshOfferButtons);
    document.addEventListener('cart:opened',  refreshOfferButtons);
    // Re-render offers when cart opens (ensures they're populated even if first open)
    document.addEventListener('cart:opened',  loadDiscounts);
    refreshOfferButtons();
  }

  /* ─── Collection page filter drawer ────────────────────────────────────── */
  function initFilterDrawer() {
    const overlay = document.querySelector('.filter-overlay');
    const drawer  = document.querySelector('.filter-drawer');
    const openBtns = document.querySelectorAll('[data-filter-open]');
    const closeBtns = document.querySelectorAll('[data-filter-close]');

    if (!drawer) return;
    const open  = () => { drawer.classList.add('open'); overlay?.classList.add('open'); lockScroll(); };
    const close = () => { drawer.classList.remove('open'); overlay?.classList.remove('open'); unlockScroll(); };

    openBtns.forEach(b => b.addEventListener('click', open));
    closeBtns.forEach(b => b.addEventListener('click', close));
    overlay?.addEventListener('click', close);
  }

  /* ─── View toggle (grid / list) ─────────────────────────────────────────── */
  function initViewToggle() {
    const grid  = document.querySelector('.products-grid');
    const btns  = document.querySelectorAll('.view-btn');
    btns.forEach(btn => {
      btn.addEventListener('click', () => {
        btns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const view = btn.dataset.view;
        if (grid) { grid.classList.toggle('list-view', view === 'list'); }
      });
    });
  }

  /* ─── Sort select ───────────────────────────────────────────────────────── */
  function initSort() {
    const sel = document.querySelector('.sort-select');
    if (!sel) return;
    sel.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sel.value);
      window.location.href = url.toString();
    });
    // Restore from URL
    const current = new URL(window.location.href).searchParams.get('sort_by');
    if (current) sel.value = current;
  }

  /* ─── Category scroll active state ─────────────────────────────────────── */
  function initCategoryScroll() {
    const items = document.querySelectorAll('.cat-item');
    const current = window.location.pathname;
    items.forEach(item => {
      if (item.href && current.includes(item.getAttribute('href'))) {
        item.classList.add('active');
      }
    });
  }

  function initScrollAnimate() {
    if (window.innerWidth < 960) return; // skip on mobile — too many simultaneous animations cause jank
    const els = document.querySelectorAll('.prod-card, .goal-card, .blog-card, .usp-card');
    if (!('IntersectionObserver' in window)) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('animate-fade-up'); obs.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -40px 0px', threshold: 0.08 });
    els.forEach(el => obs.observe(el));
  }

  /* ─── Pincode delivery check ────────────────────────────────────────────── */
  function initPincheck() {
    const form = document.querySelector('.pincode-form');
    if (!form) return;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const pin = form.querySelector('.pincode-input')?.value?.trim();
      const result = form.querySelector('.pincode-result');
      if (!pin || pin.length !== 6 || !/^\d+$/.test(pin)) {
        if (result) { result.textContent = 'Please enter a valid 6-digit pincode.'; result.style.color = '#ef4444'; }
        return;
      }
      if (result) { result.textContent = '✓ Delivery available in 3-5 days'; result.style.color = '#1d9e74'; }
    });
  }


  /* ─── Single authoritative updateCartCount ──────────────────────────── */
  async function updateCartCount() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      document.querySelectorAll('.cart-count, .cart-drawer-count').forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? 'grid' : 'none';
      });
      return cart;
    } catch (e) { return null; }
  }

  /* ─── Cart Drawer ────────────────────────────────────────────────────── */
  const FREE_SHIPPING_THRESHOLD = window.FREE_SHIPPING_THRESHOLD || 99900; // paise — set via theme settings

  function formatMoney(paise) {
    return '₹' + (paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  function updateShippingBar(total) {
    const fill = document.getElementById('csb-fill');
    const text = document.getElementById('csb-text');
    if (!fill) return;
    if (total >= FREE_SHIPPING_THRESHOLD) {
      fill.style.width = '100%';
      if (text) text.innerHTML = '🎉 You\'ve unlocked <strong>Free Shipping!</strong>';
    } else {
      const remaining = FREE_SHIPPING_THRESHOLD - total;
      const pct = Math.min((total / FREE_SHIPPING_THRESHOLD) * 100, 100);
      fill.style.width = pct + '%';
      if (text) text.innerHTML = `Add <strong>${formatMoney(remaining)}</strong> more for free shipping! 🚚`;
    }
  }

  /* Cross-sell: prefetch on page load so data is ready when drawer opens */
  let _xsellFetchPromise = null;

  function prefetchCrossSell() {
    if (_xsellFetchPromise) return;
    _xsellFetchPromise = fetch('/collections/best-sellers/products.json?limit=10')
      .then(r => r.json())
      .catch(() => ({ products: [] }));
  }

  async function renderCrossSell(cart) {
    const section = document.getElementById('cart-xsell-section');
    const track   = document.getElementById('cart-xsell-track');
    if (!section || !track) return;
    try {
      // Resolves instantly if page-load prefetch already completed
      const data = await (_xsellFetchPromise || fetch('/collections/best-sellers/products.json?limit=10').then(r => r.json()));
      const cartProductIds = new Set(cart.items.map(i => i.product_id));
      const products = (data.products || [])
        .filter(p => !cartProductIds.has(p.id))
        .slice(0, 6);
      if (!products.length) { section.style.display = 'none'; return; }
      track.innerHTML = products.map(p => {
        const v   = p.variants[0];
        const img = p.images[0]?.src ? p.images[0].src.replace(/(\.jpg|\.png|\.webp)/, '_200x$1') : '';
        return `<div class="xsell-item">
          ${img ? `<img class="xsell-item-img" src="${img}" alt="${p.title}" loading="eager">` : '<div class="xsell-item-img"></div>'}
          <div class="xsell-item-body">
            <div class="xsell-item-name">${p.title}</div>
            <div class="xsell-item-price">${formatMoney(v.price * 100)}</div>
            <button class="xsell-add-btn" data-variant="${v.id}">+ Add</button>
          </div>
        </div>`;
      }).join('');
      section.style.display = 'block';
    } catch (e) { section.style.display = 'none'; }
  }

  /* Main render — fetches /products/{handle}.js in parallel to get real compare_at_price */
  async function fetchVariantMRP(items) {
    const handles = [...new Set(items.map(i => i.handle).filter(Boolean))];
    const map = {};
    await Promise.all(handles.map(async handle => {
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) return;
        const prod = await res.json();
        (prod.variants || []).forEach(v => {
          if (v.compare_at_price && v.compare_at_price > v.price) {
            map[v.id] = v.compare_at_price;
          }
        });
      } catch (_) {}
    }));
    return map;
  }

  async function renderCartItems(cart) {
    const list   = document.getElementById('cart-items-list');
    const empty  = document.getElementById('cart-empty');
    const footer = document.getElementById('cart-drawer-footer');
    const sub    = document.getElementById('cart-subtotal');
    const count  = document.getElementById('cart-drawer-count');
    if (!list) return;

    const n = cart.item_count;
    if (count) { count.textContent = n; count.style.display = n > 0 ? 'grid' : 'none'; }
    document.querySelectorAll('.cart-count').forEach(el => {
      el.textContent = n; el.style.display = n > 0 ? 'grid' : 'none';
    });

    if (n === 0) {
      list.innerHTML = '';
      empty?.style.setProperty('display', 'block');
      footer?.classList.add('cart-footer--hidden');
      document.getElementById('cart-offers-section')?.style.setProperty('display', 'none');
      document.getElementById('cart-xsell-section')?.style.setProperty('display', 'none');
      renderCartItems._xsellDone = false;
      return;
    }

    empty?.style.setProperty('display', 'none');
    footer?.classList.remove('cart-footer--hidden');
    document.getElementById('cart-offers-section')?.style.setProperty('display', 'block');
    if (sub) sub.textContent = formatMoney(cart.total_price);
    updateShippingBar(cart.total_price);

    // Fetch real compare_at_price from product JSON (not in /cart.js response)
    const mrpMap = await fetchVariantMRP(cart.items);

    // Savings badge
    const savingsEl = document.getElementById('cart-savings');
    if (savingsEl) {
      const saved = cart.items.reduce((acc, item) => {
        const cap = mrpMap[item.variant_id] || 0;
        return cap > item.price ? acc + (cap - item.price) * item.quantity : acc;
      }, 0);
      savingsEl.textContent = saved > 0 ? '✓ ' + formatMoney(saved) + ' saved!' : '';
      savingsEl.style.display = saved > 0 ? 'inline-flex' : 'none';
    }

    // ── Price Breakdown Card ──────────────────────────────────────────────
    const mrpTotal = cart.items.reduce((sum, item) => {
      const cap = mrpMap[item.variant_id] || item.price;
      return sum + cap * item.quantity;
    }, 0);
    const productDiscount = mrpTotal - cart.total_price;
    const freeShipping    = cart.total_price >= FREE_SHIPPING_THRESHOLD;

    const bdMrp         = document.getElementById('cart-bd-mrp');
    const bdDiscRow     = document.getElementById('cart-bd-discount-row');
    const bdDiscVal     = document.getElementById('cart-bd-discount-val');
    const bdDelivery    = document.getElementById('cart-bd-delivery');
    const bdTotalDisp   = document.getElementById('cart-bd-total-display');
    const bdSub         = document.getElementById('cart-subtotal');
    const bdBreakdown   = document.getElementById('cart-breakdown');

    if (bdBreakdown)   bdBreakdown.style.display   = '';
    if (bdMrp)         bdMrp.textContent            = formatMoney(mrpTotal);
    if (bdSub)         bdSub.textContent            = formatMoney(cart.total_price);
    if (bdTotalDisp)   bdTotalDisp.textContent      = formatMoney(cart.total_price);
    if (bdDiscRow && bdDiscVal) {
      if (productDiscount > 0) {
        bdDiscVal.textContent   = `-${formatMoney(productDiscount)}`;
        bdDiscRow.style.display = '';
      } else {
        bdDiscRow.style.display = 'none';
      }
    }
    if (bdDelivery) {
      bdDelivery.innerHTML = freeShipping
        ? `<span class="cart-bd-strike">₹49</span><span class="cart-bd-free">Free</span>`
        : `<span class="cart-bd-val">₹49</span>`;
    }


    list.innerHTML = cart.items.map(item => {
      const cap     = mrpMap[item.variant_id] || 0;
      const hasMRP  = cap > item.price;
      const hasLDsc = item.original_line_price && item.original_line_price > item.final_line_price;
      const hasDisc = hasMRP || hasLDsc;
      const mrpTot  = hasMRP ? cap * item.quantity : (hasLDsc ? item.original_line_price : 0);
      const url     = item.url || `/products/${item.handle}`;
      return `<div class="cart-item" data-key="${item.key}">
        <a class="cart-item-img" href="${url}" tabindex="-1">
          ${item.image ? `<img src="${item.image}" alt="${item.title}" width="60" height="60" loading="eager">` : '🥜'}
        </a>
        <div class="cart-item-info">
          <a class="cart-item-title" href="${url}">${item.product_title}</a>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<div class="cart-item-variant">Pack: ${item.variant_title}</div>` : ''}
          <div class="cart-item-actions">
            <div class="cart-qty-ctrl" role="group" aria-label="Quantity">
              <button class="cart-qty-btn" data-key="${item.key}" data-qty="${item.quantity - 1}" aria-label="Decrease">−</button>
              <span class="cart-qty-num">${item.quantity}</span>
              <button class="cart-qty-btn" data-key="${item.key}" data-qty="${item.quantity + 1}" aria-label="Increase">+</button>
            </div>
            <div class="cart-item-pricing">
              ${hasDisc ? `<span class="cart-item-mrp">${formatMoney(mrpTot)}</span>` : ''}
              <span class="cart-item-price${hasDisc ? ' cart-item-price--sale' : ''}">${formatMoney(item.final_line_price)}</span>
            </div>
          </div>
        </div>
        <button class="cart-item-remove" data-key="${item.key}" data-qty="0" aria-label="Remove" title="Remove">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>`;
    }).join('');

    // Cross-sell only on first open per session
    if (!renderCartItems._xsellDone) {
      renderCartItems._xsellDone = true;
      renderCrossSell(cart);
    }

    // Proof rotator — start once, never stack intervals
    const proofEl = document.getElementById('cart-proof-text');
    if (proofEl && !window.__proofInterval) {
      const msgs = ['🔥 127 people ordered today','⚡ Ships within 24 hours',
                    '✅ 4.8★ · 2,400+ reviews','🎁 Free shipping above ₹' + Math.round(FREE_SHIPPING_THRESHOLD/100),'🔒 100% secure checkout'];
      let idx = 0;
      window.__proofInterval = setInterval(() => {
        idx = (idx + 1) % msgs.length;
        proofEl.style.animation = 'none';
        void proofEl.offsetHeight;
        proofEl.style.animation = '';
        proofEl.textContent = msgs[idx];
      }, 4000);
    }
    // Notify coupon system cart has re-rendered so it can re-apply discount UI
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  async function fetchAndRenderCart() {
    // Show skeleton immediately — items appear before network round-trip completes
    const list = document.getElementById('cart-items-list');
    if (list && !list.querySelector('.cart-item')) {
      list.innerHTML = Array(2).fill(0).map(() => `
        <div class="cart-skeleton-item">
          <div class="skel skel-img"></div>
          <div class="skel-body">
            <div class="skel skel-line skel-line--wide"></div>
            <div class="skel skel-line skel-line--narrow"></div>
            <div class="skel skel-line skel-line--mid"></div>
          </div>
        </div>`).join('');
    }
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      await renderCartItems(cart);
      return cart;
    } catch (e) { return null; }
  }

  async function changeCartItem(key, qty) {
    try {
      const res  = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty }),
      });
      const cart = await res.json();
      await renderCartItems(cart);
    } catch (e) { console.error('[Khari] Cart change failed:', e); }
  }

  function openCartDrawer() {
    const drawer  = document.getElementById('cart-drawer');
    const overlay = document.getElementById('cart-overlay');
    if (!drawer) return;
    // If already open, just refresh contents — don't double-lock scroll
    if (drawer.classList.contains('open')) {
      fetchAndRenderCart();
      return;
    }
    drawer.classList.add('open');
    overlay?.classList.add('open');
    lockScroll();
    drawer.setAttribute('aria-hidden', 'false');
    fetchAndRenderCart();
    document.dispatchEvent(new CustomEvent('cart:opened'));
    setTimeout(() => drawer.querySelector('[data-cart-close]')?.focus(), 150);
  }

  function closeCartDrawer() {
    const drawer  = document.getElementById('cart-drawer');
    const overlay = document.getElementById('cart-overlay');
    if (!drawer) return;
    drawer.classList.remove('open');
    overlay?.classList.remove('open');
    unlockScroll();
    drawer.setAttribute('aria-hidden', 'true');
    // Return focus to cart button if visible, silently skip if hidden (mobile)
    const navBtn = document.getElementById('cart-nav-btn');
    if (navBtn && navBtn.offsetParent !== null) navBtn.focus();
  }

  function initCartDrawer() {
    // Open triggers
    document.getElementById('cart-nav-btn')?.addEventListener('click', e => {
      e.preventDefault(); openCartDrawer();
    });
    document.querySelectorAll('a[href="/cart"]').forEach(a => {
      a.addEventListener('click', e => { e.preventDefault(); openCartDrawer(); });
    });

    // Overlay click closes drawer
    document.getElementById('cart-overlay')?.addEventListener('click', closeCartDrawer);

    // Escape key
    document.addEventListener('keydown', e => { if (e.key === 'Escape') closeCartDrawer(); });

    // ── Drawer-level delegation: close button + body actions ──────────────
    // Attaching to the whole drawer (not just body) ensures the close button
    // in the header is always caught regardless of z-index or overlap issues.
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    drawer.addEventListener('click', async e => {
      // Close button (header) — caught via data attribute, robust to z-index issues
      if (e.target.closest('[data-cart-close]')) {
        closeCartDrawer();
        return;
      }

      // Only process body actions below this point
      if (!e.target.closest('#cart-drawer-body')) return;

      // Qty stepper & remove
      const actionBtn = e.target.closest('.cart-qty-btn, .cart-item-remove');
      if (actionBtn) {
        if (actionBtn.disabled) return;
        actionBtn.disabled = true;
        await changeCartItem(actionBtn.dataset.key, parseInt(actionBtn.dataset.qty, 10));
        return;
      }
      // Cross-sell quick-add
      const xsellBtn = e.target.closest('.xsell-add-btn');
      if (xsellBtn && !xsellBtn.disabled) {
        xsellBtn.disabled = true; xsellBtn.textContent = '✓ Added';
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(xsellBtn.dataset.variant, 10), quantity: 1 }] })
        });
        renderCartItems._xsellDone = false;
        await fetchAndRenderCart();
        return;
      }
      // Offer copy
      const copyBtn = e.target.closest('.cart-offer-copy');
      if (copyBtn) {
        const code = copyBtn.dataset.code;
        if (!code) return;
        navigator.clipboard?.writeText(code).catch(() => {});
        const orig = copyBtn.innerHTML;
        copyBtn.classList.add('copied');
        copyBtn.innerHTML = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>`;
        setTimeout(() => { copyBtn.innerHTML = orig; copyBtn.classList.remove('copied'); }, 2000);
      }
    });
  }

  /* ─── Global Add to Cart listener for all .add-btn & [data-variant-id] ─── */
  function initGlobalAddToCart() {
    window.KhariCart = {
      openDrawer: openCartDrawer,
      closeDrawer: closeCartDrawer,
      fetchCart: fetchAndRenderCart
    };

    document.addEventListener('click', async function(e) {
      const btn = e.target.closest('[data-variant-id], .add-btn, .snack-add-btn, .pmc-add-btn, .spotlight-atc-btn');
      if (!btn) return;
      if (btn.id && btn.id.includes('srm-winner-add-btn')) return;

      const variantId = btn.getAttribute('data-variant-id') || btn.dataset.variantId;
      if (!variantId) return;

      e.preventDefault();
      e.stopPropagation();

      if (btn.disabled) return;
      btn.disabled = true;

      const origText = btn.innerHTML;
      btn.innerHTML = '<span>⏳ Adding...</span>';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
        });
        if (!res.ok) throw new Error('Cart add failed');
        btn.innerHTML = '<span>✓ Added!</span>';

        await updateCartCount();
        openCartDrawer();
      } catch (err) {
        console.error('[Khari] Add to cart failed:', err);
        btn.innerHTML = '<span>Error! Try again</span>';
      } finally {
        setTimeout(() => {
          btn.innerHTML = origText;
          btn.disabled = false;
        }, 1200);
      }
    });
  }

  /* ─── Init ──────────────────────────────────────────────────────────── */
  function init() {
    // prefetchCrossSell deferred to window.load — runs after critical resources
    initMobileNav();
    initScarcityBadges();
    initWishlist();
    initQtySelectors();
    initGallery();
    initTabs();
    initVariants();
    initStickyAtc();
    initReviewsPaginator();
    initOfferCopy();
    initFilterDrawer();
    initViewToggle();
    initSort();
    initCategoryScroll();
    initScrollAnimate();
    initPincheck();
    initCartDrawer();
    initCartCoupon();
    initScrollGrids();
    initGlobalAddToCart();
    loadDiscounts(); // fetch from Shopify Discounts API via Worker

    updateCartCount();
  }

  /* ─── Auto-scroll homepage product grids ───────────────────────────────── */
  function initScrollGrids() {
    document.querySelectorAll('.products-grid--scroll').forEach(grid => {
      let paused = false;
      let timer;

      const advance = () => {
        if (paused) return;
        const card     = grid.querySelector('.prod-card');
        if (!card) return;
        const cardW    = card.offsetWidth + 12; // width + gap
        const maxLeft  = grid.scrollWidth - grid.clientWidth;

        if (grid.scrollLeft >= maxLeft - 2) {
          // Loop back to start smoothly
          grid.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          grid.scrollBy({ left: cardW, behavior: 'smooth' });
        }
      };

      const start = () => { timer = setInterval(advance, 3500); };
      const stop  = () => { clearInterval(timer); };

      // Pause on hover / touch
      grid.addEventListener('mouseenter', () => { paused = true;  stop(); });
      grid.addEventListener('mouseleave', () => { paused = false; start(); });
      grid.addEventListener('touchstart', () => { paused = true;  stop(); },  { passive: true });
      grid.addEventListener('touchend',   () => {
        // Resume after a short delay to let snap finish
        setTimeout(() => { paused = false; start(); }, 2000);
      }, { passive: true });

      // Kick off after a short delay so page has settled
      setTimeout(start, 2000);
    });
  }

  /* ─── Cart coupon / discount code (Storefront API) ─────────────────────── */
  function initCartCoupon() {
    const box         = document.getElementById('cart-coupon-box');
    const input       = document.getElementById('cart-coupon-input');
    const applyBtn    = document.getElementById('cart-coupon-apply-btn');
    const msg         = document.getElementById('cart-coupon-msg');
    const discRow     = document.getElementById('cart-discount-row');
    const discLabel   = document.getElementById('cart-discount-label');
    const discVal     = document.getElementById('cart-discount-val');
    const subtotalEl  = document.getElementById('cart-subtotal');
    if (!box) return;


    // ── Apply discount — direct checkout URL approach ──────────────────────
    // Storefront API cartDiscountCodesUpdate requires specific token scopes
    // that are not available. Discount is reliably applied via ?discount= param.
    async function validateAndApply(code) {
      setLoading(true);
      try {
        // ── Cart condition check ─────────────────────────────────────────────
        const offerBtn = [...document.querySelectorAll('.cof-tap-apply-btn')]
          .find(b => b.dataset.code === code);
        if (offerBtn) {
          const min = parseInt(offerBtn.dataset.min || '0', 10);
          if (min > 0) {
            const cart = await fetch('/cart.js').then(r => r.json());
            const totalRupees = Math.floor(cart.total_price / 100);
            if (totalRupees < min) {
              showError(`Add ₹${min - totalRupees} more to use "${code}".`);
              setLoading(false);
              return;
            }
          }
        }

        // ── Compute discount amount ──────────────────────────────────────────
        const cart         = await fetch('/cart.js').then(r => r.json());
        const cartTotal    = Math.floor(cart.total_price / 100); // rupees
        const discountPct  = offerBtn ? parseFloat(offerBtn.dataset.discountPct || '0') : 0;
        const discountAmt  = discountPct > 0 ? Math.round(cartTotal * discountPct / 100) : 0;
        const newTotal     = cartTotal - discountAmt;

        // ── Apply discount to Shopify session (sets cookie Razorpay reads) ──
        // /discount/CODE sets a server-side session cookie so the cart total
        // is already discounted when Razorpay reads it at checkout.
        try {
          await fetch(`/discount/${encodeURIComponent(code)}`, {
            method: 'GET',
            credentials: 'same-origin',
            redirect: 'follow',
          });
        } catch (_) { /* non-blocking — proceed even if fetch fails */ }

        // Also pass via hidden form input as fallback
        const discountInput = document.getElementById('cart-discount-hidden');
        if (discountInput) discountInput.value = code;
        const checkoutBtn = document.getElementById('cart-checkout-link');
        if (checkoutBtn) {
          checkoutBtn.classList.remove('animate-pulse');
          void checkoutBtn.offsetWidth;
          checkoutBtn.classList.add('animate-pulse');
          setTimeout(() => checkoutBtn.classList.remove('animate-pulse'), 1000);
        }

        // ── Update price breakdown coupon row ────────────────────────────────
        const bdCouponRow  = document.getElementById('cart-bd-coupon-row');
        const bdCouponCode = document.getElementById('cart-bd-coupon-code');
        const bdCouponVal  = document.getElementById('cart-bd-coupon-val');
        if (bdCouponRow) {
          if (bdCouponCode) bdCouponCode.textContent = code;
          if (bdCouponVal)  bdCouponVal.textContent  = discountAmt > 0 ? `-₹${discountAmt}` : 'Applied ✓';
          bdCouponRow.style.display = '';
          bdCouponRow.classList.remove('animate-in');
          void bdCouponRow.offsetWidth;
          bdCouponRow.classList.add('animate-in');
        }

        // ── Update totals ────────────────────────────────────────────────────
        if (discountAmt > 0) {
          // Sticky footer total
          const subtotalEl = document.getElementById('cart-subtotal');
          if (subtotalEl) {
            subtotalEl.textContent = `₹${newTotal}`;
            subtotalEl.classList.remove('animate-total');
            void subtotalEl.offsetWidth;
            subtotalEl.classList.add('animate-total');
            setTimeout(() => subtotalEl.classList.remove('animate-total'), 800);
          }
          // Breakdown total
          const bdTotal = document.getElementById('cart-bd-total-display');
          if (bdTotal) {
            bdTotal.textContent = `₹${newTotal}`;
            bdTotal.classList.remove('animate-price');
            void bdTotal.offsetWidth;
            bdTotal.classList.add('animate-price');
            setTimeout(() => bdTotal.classList.remove('animate-price'), 600);
          }
        }

        // ── Lock input so only one coupon at a time ─────────────────────────
        if (input)    { input.disabled = true; input.value = code; }
        if (applyBtn) {
          applyBtn.textContent         = 'Applied ✓';
          applyBtn.style.background    = '#1d9e74';
          applyBtn.style.pointerEvents = 'none';
          applyBtn.disabled            = true;
        }
        // Lock ALL offer buttons (only one coupon at a time)
        document.querySelectorAll('.cof-tap-apply-btn').forEach(btn => {
          btn.textContent = btn.dataset.code === code ? 'Applied ✓' : 'Tap to apply';
          btn.classList.toggle('applied', btn.dataset.code === code);
          btn.disabled = true;
        });
        // Show remove button
        const removeBtn = document.getElementById('cart-coupon-remove-btn');
        if (removeBtn) removeBtn.style.display = 'inline-block';

        const savingsMsg = discountAmt > 0 ? ` — you save ₹${discountAmt}!` : '';
        showSuccess(`✓ "${code}" applied${savingsMsg} Discount confirmed at checkout.`);

      } catch (err) {
        showError(err.message || 'Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    // ── UI helpers ─────────────────────────────────────────────────────────
    function setLoading(on) {
      if (!applyBtn) return;
      applyBtn.textContent = on ? 'Applying…' : 'Apply';
      applyBtn.disabled    = on;
    }
    function showError(text)   { if (msg) { msg.textContent = text; msg.className = 'cart-coupon-msg error'; } }
    function showSuccess(text) { if (msg) { msg.textContent = text; msg.className = 'cart-coupon-msg success'; } }

    function resetCouponUI() {
      if (input)    { input.value = ''; input.disabled = false; }
      if (msg)      { msg.textContent = ''; msg.className = 'cart-coupon-msg'; }
      if (applyBtn) {
        applyBtn.textContent      = 'Apply';
        applyBtn.style.background = '';
        applyBtn.style.pointerEvents = '';
        applyBtn.disabled         = false;
      }
      // Hide remove button
      const removeBtn = document.getElementById('cart-coupon-remove-btn');
      if (removeBtn) removeBtn.style.display = 'none';
      // Clear Shopify discount session cookie
      fetch('/discount/remove', { credentials: 'same-origin' }).catch(() => {});
      // Clear discount hidden input
      const discountInput = document.getElementById('cart-discount-hidden');
      if (discountInput) discountInput.value = '';
      // Hide coupon breakdown row
      const bdCouponRow = document.getElementById('cart-bd-coupon-row');
      if (bdCouponRow)  bdCouponRow.style.display = 'none';
      // Restore original cart total from Shopify
      fetch('/cart.js').then(r => r.json()).then(cart => {
        const original = Math.floor(cart.total_price / 100);
        const subtotalEl = document.getElementById('cart-subtotal');
        const bdTotal    = document.getElementById('cart-bd-total-display');
        if (subtotalEl) subtotalEl.textContent = `₹${original}`;
        if (bdTotal)    bdTotal.textContent    = `₹${original}`;
        // Re-enable offer buttons based on cart total
        refreshOfferButtons();
      }).catch(() => refreshOfferButtons());
    }

    // ── Event listeners ────────────────────────────────────────────────────
    applyBtn?.addEventListener('click', () => {
      const code = (input?.value || '').trim().toUpperCase();
      if (!code) { showError('Please enter a discount code.'); return; }
      validateAndApply(code);
    });

    input?.addEventListener('keydown', e => {
      if (e.key === 'Enter') applyBtn?.click();
    });

    document.getElementById('cart-coupon-remove-btn')?.addEventListener('click', () => {
      resetCouponUI();
    });

    // Clear all stale coupon keys from old implementation
    localStorage.removeItem('khari_applied_code');
    localStorage.removeItem('khari_pending_code');
    localStorage.removeItem('khari_offer_code');
    localStorage.removeItem('khari_offer_savings');
  }
  return { init, addToCart, openCartDrawer, prefetchCrossSell };

})();

document.addEventListener('DOMContentLoaded', Khari.init);
// Prefetch cross-sell data after page fully loads — doesn't compete with critical resources
window.addEventListener('load', () => Khari.prefetchCrossSell());
